import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { FileText, Globe } from 'lucide-react';

import Button from '../../../components/ui/button';
import Input from '../../../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import wizardService from '../../../services/wizardService';
import templateService from '../../../services/templateService';

const TEMPLATE_CATEGORIES = [
  { id: 'coding', name: 'Coding', description: 'Code review, generation, and analysis' },
  { id: 'text', name: 'Text', description: 'General text processing and manipulation' },
  { id: 'health', name: 'Healthcare', description: 'Medical summaries and clinical notes' },
  { id: 'legal', name: 'Legal', description: 'Contract analysis and legal document processing' },
  { id: 'finance', name: 'Finance', description: 'Financial report analysis and insights' },
  { id: 'support', name: 'Customer Support', description: 'Ticket classification and response generation' },
  { id: 'business', name: 'Business', description: 'Professional communication and translation' },
  { id: 'essay', name: 'Essay', description: 'Essay writing and analysis' },
  { id: 'novel', name: 'Novel', description: 'Creative writing and storytelling' }
];

const TemplateSelection = ({
  currentStepData,
  onStepDataChange,
  onNext,
  saving
}) => {
  const [selectionType, setSelectionType] = useState(currentStepData?.type || null);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(currentStepData?.template || null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [customName, setCustomName] = useState(currentStepData?.custom_name || '');
  const [loading, setLoading] = useState(false);

  // Load templates when selection type changes
  useEffect(() => {
    const loadTemplates = async () => {
      if (selectionType !== 'template') return;

      try {
        setLoading(true);
        const response = await wizardService.getEvaluationTemplates();

        if (response.success !== false) {
          setTemplates(response.data || response.templates || []);
        } else {
          // Fallback to template service - load both public and user's private templates
          const fallbackResponse = await templateService.getTemplates();
          setTemplates(fallbackResponse.templates || fallbackResponse.data || []);
        }
      } catch (error) {
        console.error('Failed to load templates:', error);
        toast.error('Failed to load templates');
      } finally {
        setLoading(false);
      }
    };

    loadTemplates();
  }, [selectionType]);

  // Filter templates based on search and category
  const filteredTemplates = templates.filter(template => {
    const matchesSearch = !searchTerm ||
      template.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.description?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesCategory = !selectedCategory || template.category === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  // Handle selection type change
  const handleSelectionTypeChange = (type) => {
    setSelectionType(type);
    setSelectedTemplate(null);
    setCustomName('');

    const stepData = {
      type,
      template: null,
      custom_name: type === 'custom' ? '' : undefined
    };

    onStepDataChange(stepData);
  };

  // Handle template selection
  const handleTemplateSelection = (template) => {
    setSelectedTemplate(template);

    const stepData = {
      type: 'template',
      template_id: template.id,
      template: template
    };

    onStepDataChange(stepData);
  };

  // Handle custom name change
  const handleCustomNameChange = (e) => {
    const name = e.target.value;
    setCustomName(name);

    const stepData = {
      type: 'custom',
      custom_name: name
    };

    onStepDataChange(stepData);
  };

  // Render template selection UI
  const renderTemplateSelection = () => {
    if (loading && templates.length === 0) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <span className="ml-2 text-muted-foreground">Loading templates...</span>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="flex items-center space-x-2">
          <Input
            type="text"
            placeholder="Search templates..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-grow"
            disabled={loading}
          />
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="border rounded px-2 py-1"
            disabled={loading}
          >
            <option value="">All Categories</option>
            {TEMPLATE_CATEGORIES.map(category => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTemplates.map(template => (
            <Card
              key={template.id}
              className={`cursor-pointer hover:border-primary transition-all ${selectedTemplate?.id === template.id ? 'border-primary border-2' : ''
                }`}
              onClick={() => handleTemplateSelection(template)}
            >
              <CardHeader>
                <CardTitle>{template.name}</CardTitle>
                <CardDescription>{template.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-muted-foreground">
                    Category: {typeof template.category === 'object' ? template.category.name || template.category.label || 'Unknown' : template.category}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="p-8 space-y-8">
      <div className="space-y-4">
        <h2 className="text-2xl font-bold">Evaluation Template Selection</h2>
        <p className="text-muted-foreground">
          Choose how you want to create your evaluation
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card
            className={`cursor-pointer hover:border-primary transition-all ${selectionType === 'custom' ? 'border-primary border-2' : ''
              }`}
            onClick={() => handleSelectionTypeChange('custom')}
          >
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <FileText className="w-6 h-6" />
                <span>Custom Evaluation</span>
              </CardTitle>
              <CardDescription>
                Create a completely custom evaluation from scratch
              </CardDescription>
            </CardHeader>
          </Card>

          <Card
            className={`cursor-pointer hover:border-primary transition-all ${selectionType === 'template' ? 'border-primary border-2' : ''
              }`}
            onClick={() => handleSelectionTypeChange('template')}
          >
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Globe className="w-6 h-6" />
                <span>Use Existing Template</span>
              </CardTitle>
              <CardDescription>
                Select from pre-defined evaluation templates
              </CardDescription>
            </CardHeader>
          </Card>
        </div>

        {selectionType === 'custom' && (
          <div className="mt-4">
            <Input
              type="text"
              placeholder="Enter a name for your custom evaluation"
              value={customName}
              onChange={handleCustomNameChange}
              className="w-full"
            />
          </div>
        )}
      </div>

      {selectionType === 'template' && renderTemplateSelection()}

      <div className="flex flex-col items-end space-y-2">
        {selectionType === 'template' && !selectedTemplate && (
          <span className="text-sm text-red-500">Please select a template to proceed</span>
        )}
        <Button
          variant="outline"
          onClick={() => {
            // Ensure the current step data is passed before proceeding
            if (selectionType === 'template' && selectedTemplate) {
              const data = {
                type: 'template',
                template_id: selectedTemplate.id,
                template: selectedTemplate
              };
              onStepDataChange(data);
              onNext(data);
            } else if (selectionType === 'custom' && customName.trim()) {
              const data = {
                type: 'custom',
                custom_name: customName.trim()
              };
              onStepDataChange(data);
              onNext(data);
            }
          }}
          disabled={
            !selectionType ||
            (selectionType === 'template' && !selectedTemplate) ||
            (selectionType === 'custom' && !customName.trim())
          }
        >
          Next
        </Button>
      </div>
    </div>
  );
};

export default TemplateSelection;
